import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc } from "@/lib/api";
import { fmtPrice } from "@/lib/cart";
import { Reveal } from "@/components/Reveal";

export function ProductImage({ p, className = "" }) {
  if (p.image) {
    return <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className={`${className} object-cover img-zoom`} />;
  }
  if (p.kind === "Gift Card") {
    return (
      <div className={`${className} bg-gradient-to-br from-pool via-pool-dark to-pool-deep flex flex-col items-center justify-center p-6`}>
        <img src="/logo.png" alt="" aria-hidden="true" className="h-12 w-auto mb-3 brightness-0 invert" />
        <p className="text-white font-display font-black tracking-[0.2em] text-sm text-center">POOLSIDE GIFT CARD</p>
        <p className="text-white/70 text-[10px] tracking-[0.25em] uppercase mt-2">Digital · Delivered by email</p>
      </div>
    );
  }
  return (
    <div className={`${className} bg-pool-light/40 flex flex-col items-center justify-center p-6`}>
      <img src="/logo.png" alt="" aria-hidden="true" className="h-14 w-auto opacity-70" />
      <p className="text-pool-deep/50 text-[10px] tracking-[0.25em] uppercase font-bold mt-3">Photo coming soon</p>
    </div>
  );
}

function CollectionCard({ p, index }) {
  const comingSoon = p.status === "COMING SOON";
  const priceCents = Math.round((p.price || 0) * 100);

  return (
    <Reveal delay={(index % 5) * 0.06}>
      <Link to={`/shop/${p.id}`} className="group block" data-testid={`product-card-${p.id}`}>
        <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[#f4f1ec]" data-testid={`product-image-${p.id}`}>
          {p.image && p.image_2 ? (
            <>
              <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover img-zoom transition-opacity duration-500 group-hover:opacity-0" />
              <img src={imgSrc(p.image_2)} alt={`${p.name} — alternate view`} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100" data-testid={`product-image-alt-${p.id}`} />
            </>
          ) : (
            <ProductImage p={p} className="absolute inset-0 h-full w-full" />
          )}
          {comingSoon && (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 text-ink text-[9px] tracking-[0.22em] uppercase font-bold px-4 py-1.5 rounded-full shadow-sm whitespace-nowrap" data-testid={`product-comingsoon-${p.id}`}>
              Coming Soon
            </span>
          )}
          {p.status === "SOLD OUT" && (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-ink text-white text-[9px] tracking-[0.2em] uppercase font-bold px-4 py-1.5 rounded-full whitespace-nowrap" data-testid={`product-soldout-${p.id}`}>
              Sold Out
            </span>
          )}
          {p.status === "AVAILABLE" && p.external_link && (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-pool text-white text-[9px] tracking-[0.22em] uppercase font-bold px-4 py-1.5 rounded-full shadow-sm whitespace-nowrap" data-testid={`product-available-${p.id}`}>
              Available Now
            </span>
          )}
        </div>
        <p className="mt-3.5 text-center font-display font-bold text-ink text-sm leading-tight group-hover:text-pool transition-colors" data-testid={`product-name-${p.id}`}>
          {p.name}
        </p>
        {p.status === "AVAILABLE" && priceCents > 0 && (
          <p className="mt-1 text-center text-slate-500 text-xs font-semibold" data-testid={`product-price-${p.id}`}>{fmtPrice(priceCents)}</p>
        )}
      </Link>
    </Reveal>
  );
}

function BeFirstInLine() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email, source: "Shop – Merchandise Launch" });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email address");
    }
    setBusy(false);
  };

  return (
    <section className="relative overflow-hidden bg-pool-light/50" data-testid="shop-be-first">
      <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10 pointer-events-none" />
      <div className="relative container-site py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <h2 className="font-hero font-black tracking-tight leading-[1.0] text-4xl sm:text-5xl text-ink" data-testid="shop-be-first-headline">
            BE <span className="italic text-pool">FIRST</span> IN LINE.
          </h2>
          <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed max-w-md" data-testid="shop-be-first-copy">
            The Poolside Collection is coming soon. Join Stay Poolside for first access, launch updates &amp; exclusive drops.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          {done ? (
            <p className="inline-flex items-center gap-2.5 bg-white border border-pool/30 text-pool-deep font-semibold text-sm px-7 py-4 rounded-full" data-testid="shop-join-confirmation">
              <Check className="h-4 w-4 text-pool" /> You&rsquo;re Poolside. We&rsquo;ll keep you in the loop.
            </p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="shop-join-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                aria-label="Email address"
                className="flex-1 bg-white border border-slate-200 text-ink placeholder:text-slate-400 text-sm px-5 py-4 rounded-full focus:outline-none focus:ring-2 focus:ring-pool/50 focus:border-pool"
                data-testid="shop-join-email"
              />
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark disabled:opacity-60 text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-colors whitespace-nowrap"
                data-testid="shop-join-submit"
              >
                {busy ? "Joining…" : <>Join the List <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
          <p className="mt-4 text-[10px] tracking-[0.25em] uppercase font-bold text-slate-400" data-testid="shop-be-first-note">
            First access. New drops. All things Poolside.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="shop-page">
      {/* HERO — poolside water backdrop */}
      <section className="relative overflow-hidden" data-testid="shop-hero">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.55),rgba(255,255,255,0.05)_62%)] pointer-events-none" aria-hidden="true" />
        <div className="relative container-site py-20 sm:py-24 text-center max-w-3xl mx-auto">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-6" data-testid="shop-eyebrow">
              <span className="h-px w-10 bg-ink/50" aria-hidden="true" />
              <p className="text-[10px] sm:text-xs tracking-[0.35em] uppercase font-bold text-ink">Shop Poolside</p>
              <span className="h-px w-10 bg-ink/50" aria-hidden="true" />
            </div>
            <h1 className="font-hero font-black tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-ink" data-testid="shop-headline">
              THE POOLSIDE<br />
              <span className="italic text-pool">COLLECTION</span>
            </h1>
            <p className="mt-5 text-ink/70 leading-relaxed text-sm sm:text-base font-medium" data-testid="shop-subcopy">
              Good food. Good vibes. And a little Poolside to take with you.
            </p>
            <span className="mt-6 inline-block bg-pool text-white text-[10px] tracking-[0.28em] uppercase font-bold px-6 py-2.5 rounded-full shadow-lg" data-testid="shop-coming-soon-label">
              Coming Soon
            </span>
          </Reveal>
        </div>
      </section>

      {/* COLLECTION GRID */}
      <section className="bg-white py-14 sm:py-16" data-testid="shop-grid-section">
        <div className="container-site">
          {products.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-16" data-testid="shop-empty">The collection is being prepared — check back soon.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 sm:gap-6" data-testid="shop-grid">
              {products.map((p, i) => (
                <CollectionCard key={p.id} p={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <BeFirstInLine />
    </div>
  );
}
